import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import FieldMappingInput from '../targets/FieldMappingInput';
import {
  MAX_BANDS,
  MAX_RULES,
  MAX_VALUES,
  canTranslate,
  emptyRule,
  isDatasetPath,
  ruleProblems,
} from '../../utils/datasetMappingUtils';
import type { RuleForm, RulePlace, Translation } from '../../utils/datasetMappingUtils';
import type { ColumnType } from '../../utils/datasetFormUtils';

interface DatasetValueMapDialogProps {
  column: string;
  columnType: ColumnType;
  /** Whether the column is a key or the ordering field, and whether the dataset has a row path. */
  place: RulePlace;
  /** The rules the column reads with now; a plain path comes in as one rule. */
  rules: RuleForm[];
  rowSample: Record<string, unknown>;
  onApply: (rules: RuleForm[]) => void;
  /** Turns the column back into a plain path; absent when the rules are saved. */
  onUsePath?: () => void;
  onClose: () => void;
}

const acceptsMappingPath = (path: string) => isDatasetPath(path, true);

interface RuleCardProps {
  rule: RuleForm;
  index: number;
  last: boolean;
  /** Fewer than two rules, or a saved rule, cannot be removed. */
  removable: boolean;
  columnType: ColumnType;
  /** Values and bands are offered: the column is not a list, JSON, a key or the ordering field. */
  translatable: boolean;
  rowSample: Record<string, unknown>;
  errors: Record<string, string>;
  onChange: (rule: RuleForm) => void;
  onRemove: () => void;
}

/** What a translation becomes, typed to the column: true or false for a boolean column. */
const OutputField: React.FC<{ label: string; value: string; type: ColumnType; locked: boolean; error?: string; onChange: (v: string) => void }> = ({
  label, value, type, locked, error, onChange,
}) => (
  <TextField
    select={type === 'boolean'}
    size="small"
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    disabled={locked}
    error={!!error}
    helperText={error}
    SelectProps={{ native: true }}
    InputLabelProps={{ shrink: true }}
    sx={{ flex: 1 }}
  >
    {type === 'boolean' && ['', 'true', 'false'].map((v) => <option key={v} value={v}>{v}</option>)}
  </TextField>
);

const RuleCard: React.FC<RuleCardProps> = ({ rule, index, last, removable, columnType, translatable, rowSample, errors, onChange, onRemove }) => {
  const [skipText, setSkipText] = useState('');
  const label = `Rule ${index + 1}`;
  const at = `rule.${index}`;
  const set = (changes: Partial<RuleForm>) => onChange({ ...rule, ...changes });
  const setValue = (i: number, changes: Partial<RuleForm['values'][number]>) =>
    set({ values: rule.values.map((v, n) => (n === i ? { ...v, ...changes } : v)) });
  const setBand = (i: number, changes: Partial<RuleForm['bands'][number]>) =>
    set({ bands: rule.bands.map((b, n) => (n === i ? { ...b, ...changes } : b)) });
  const addSkip = () => {
    if (skipText && !rule.skip.includes(skipText)) set({ skip: [...rule.skip, skipText] });
    setSkipText('');
  };
  return (
    <Box data-testid={`dataset-rule-${index}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>{label}</Typography>
        {removable && (
          <IconButton size="small" onClick={onRemove} aria-label={`Remove rule ${index + 1}`}><DeleteIcon fontSize="small" /></IconButton>
        )}
      </Box>
      <FieldMappingInput
        label={`${label} path`}
        value={rule.path}
        onChange={(path) => set({ path })}
        sampleEvent={rowSample}
        disabled={rule.locked}
        error={errors[`${at}.path`]}
        pathFilter={acceptsMappingPath}
      />
      <TextField
        select
        size="small"
        label={`${label} translation`}
        value={rule.translate}
        onChange={(e) => set({ translate: e.target.value as Translation })}
        disabled={rule.locked}
        error={!!errors[`${at}.translate`]}
        helperText={errors[`${at}.translate`]}
        SelectProps={{ native: true }}
        InputLabelProps={{ shrink: true }}
        sx={{ maxWidth: 240 }}
      >
        <option value="none">Read as it is</option>
        {(translatable || rule.translate !== 'none') && <option value="values">Value list</option>}
        {(translatable || rule.translate !== 'none') && <option value="bands">Number bands</option>}
      </TextField>

      {rule.translate === 'values' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rule.values.map((v, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                size="small"
                label={`${label} value ${i + 1} from`}
                value={v.from}
                onChange={(e) => setValue(i, { from: e.target.value })}
                disabled={v.locked}
                error={!!errors[`${at}.value.${i}.from`]}
                helperText={errors[`${at}.value.${i}.from`]}
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
              <OutputField
                label={`${label} value ${i + 1} to`}
                value={v.to}
                type={columnType}
                locked={v.locked}
                error={errors[`${at}.value.${i}.to`]}
                onChange={(to) => setValue(i, { to })}
              />
              {!v.locked && (
                <IconButton size="small" onClick={() => set({ values: rule.values.filter((_, n) => n !== i) })} aria-label={`Remove value ${i + 1} of rule ${index + 1}`}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          {errors[`${at}.values`] && <Typography variant="caption" color="error">{errors[`${at}.values`]}</Typography>}
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => set({ values: [...rule.values, { from: '', to: '', locked: false }] })}
            disabled={rule.values.length >= MAX_VALUES}
            aria-label={`Add value to rule ${index + 1}`}
          >
            Add value
          </Button>
        </Box>
      )}

      {rule.translate === 'bands' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">A number from min to max, both included, becomes the value.</Typography>
          {rule.bands.map((b, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              {(['min', 'max'] as const).map((end) => (
                <TextField
                  key={end}
                  size="small"
                  label={`${label} band ${i + 1} ${end}`}
                  value={b[end]}
                  onChange={(e) => setBand(i, { [end]: e.target.value })}
                  disabled={b.locked}
                  error={!!errors[`${at}.band.${i}.${end}`]}
                  helperText={errors[`${at}.band.${i}.${end}`]}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 120 }}
                />
              ))}
              <OutputField
                label={`${label} band ${i + 1} to`}
                value={b.to}
                type={columnType}
                locked={b.locked}
                error={errors[`${at}.band.${i}.to`]}
                onChange={(to) => setBand(i, { to })}
              />
              {!b.locked && (
                <IconButton size="small" onClick={() => set({ bands: rule.bands.filter((_, n) => n !== i) })} aria-label={`Remove band ${i + 1} of rule ${index + 1}`}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
          {errors[`${at}.bands`] && <Typography variant="caption" color="error">{errors[`${at}.bands`]}</Typography>}
          <Button
            size="small"
            startIcon={<AddIcon />}
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => set({ bands: [...rule.bands, { min: '', max: '', to: '', locked: false }] })}
            disabled={rule.bands.length >= MAX_BANDS}
            aria-label={`Add band to rule ${index + 1}`}
          >
            Add band
          </Button>
        </Box>
      )}

      {(!last || rule.skip.length > 0) && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Skip to the next rule on these values
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            {rule.skip.map((s) => (
              <Chip
                key={s}
                label={s}
                size="small"
                onDelete={rule.locked ? undefined : () => set({ skip: rule.skip.filter((x) => x !== s) })}
              />
            ))}
            {!rule.locked && (
              <>
                <TextField
                  size="small"
                  label={`${label} skip value`}
                  value={skipText}
                  onChange={(e) => setSkipText(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ width: 180 }}
                />
                <Button size="small" onClick={addSkip} disabled={!skipText} aria-label={`Add skip value to rule ${index + 1}`}>Add</Button>
              </>
            )}
          </Box>
          {errors[`${at}.skip`] && <Typography variant="caption" color="error">{errors[`${at}.skip`]}</Typography>}
        </Box>
      )}
    </Box>
  );
};

/** The rules a column reads with in one input, tried in order: each a path, and what its values become. */
const DatasetValueMapDialog: React.FC<DatasetValueMapDialogProps> = ({
  column, columnType, place, rules, rowSample, onApply, onUsePath, onClose,
}) => {
  const [draft, setDraft] = useState<RuleForm[]>(rules);
  // Refusals show once Apply was tried (FE-07).
  const [attempted, setAttempted] = useState(false);
  const errors = attempted ? ruleProblems(draft, columnType, place) : {};
  // Saved rules are fixed in number and order: a rule added or removed changes what saved rows read.
  const saved = draft.some((r) => r.locked);

  const apply = () => {
    setAttempted(true);
    if (Object.keys(ruleProblems(draft, columnType, place)).length === 0) onApply(draft);
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>Translate {column || 'column'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          Rules are tried in order. A rule with no value at its path passes to the next. A value a rule does not list
          does not fit, unless the rule skips it.
        </Typography>
        {draft.map((rule, i) => (
          <RuleCard
            key={i}
            rule={rule}
            index={i}
            last={i === draft.length - 1}
            removable={draft.length > 1 && !saved}
            columnType={columnType}
            translatable={canTranslate(columnType) && !place.identifies}
            rowSample={rowSample}
            errors={errors}
            onChange={(next) => setDraft(draft.map((r, n) => (n === i ? next : r)))}
            onRemove={() => setDraft(draft.filter((_, n) => n !== i))}
          />
        ))}
        {errors.rules && <Typography variant="caption" color="error">{errors.rules}</Typography>}
        <Button
          size="small"
          startIcon={<AddIcon />}
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => setDraft([...draft, emptyRule()])}
          disabled={saved || draft.length >= MAX_RULES}
        >
          Add rule
        </Button>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {onUsePath && !saved && <Button size="small" onClick={onUsePath} sx={{ mr: 'auto' }}>Use a plain path</Button>}
        <Button size="small" variant="outlined" onClick={onClose}>Cancel</Button>
        <Button size="small" variant="contained" onClick={apply}>Apply</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DatasetValueMapDialog;
